import { Component, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PaginationConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { ChargeStatus } from '../../../../core/enums/data.enums';
import { PropertyChargeListViewLocations } from '../../../../core/enums/base.enums';
import { PropertyChargeFilter } from '../../../../core/filters/property-charge.filter';
import { PropertyChargeSchedulesFilter } from '../../../../core/filters/property-charge-schedule.filter';
import { PropertyChargeScheduleListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ChargeBulkSummaryModel } from '../../../../core/models/data/charge-bulk-summary.model';
import { CurrencyModel } from '../../../../core/models/data/currency.model';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { DateUtilities, DateFormat } from '../../../../core/utilities/date.utilities';
import { PropertyChargeListComponent } from '../../components/property-charge-list/property-charge-list.component';
import { InvoiceAgingBucket, InvoiceAgingSummaryCardsComponent } from '../../components/invoice-aging-summary-cards/invoice-aging-summary-cards.component';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-invoice-dashboard-page',
  imports: [
    PageTitleComponent,
    CommonModule,
    PropertyChargeListComponent,
    InvoiceAgingSummaryCardsComponent
  ],
  templateUrl: './invoice-dashboard.page.html',
  styleUrl: './invoice-dashboard.page.scss'
})
export class InvoiceDashboardPage implements OnInit {

  //#region Properties & Signals

  private chargesService = inject(ChargesService);
  private userSessionService = inject(UserSessionService);

  public PropertyChargeListViewLocations = PropertyChargeListViewLocations;

  public summary = signal<ChargeBulkSummaryModel | undefined>(undefined);
  public isSummaryLoading = signal<boolean>(false);
  public schedules = signal<{ id: number, name: string }[]>([]);
  public currency = signal<CurrencyModel | undefined>(undefined);
  public selectedBucket = signal<InvoiceAgingBucket>('all');
  private skipNextSummaryReload = false;

  @ViewChild(PropertyChargeListComponent) chargeListRef?: PropertyChargeListComponent;

  constructor() {
    effect(() => {
      const org = this.userSessionService.activeOrganization();
      if (org) {
        this.loadSchedules();
      }
    });

    effect(() => {
      const settings = this.userSessionService.activeOrganizationSettings();
      if (settings?.currency) {
        this.currency.set(settings.currency);
      }
    });
  }

  //#endregion

  //#region Lifecycle

  public async ngOnInit(): Promise<void> {
    await this.userSessionService.getOrganizationSettings();
  }

  //#endregion

  //#region Data Loading

  private async loadSchedules(): Promise<void> {
    const org = this.userSessionService.activeOrganization();
    if (!org) return;

    const filter: PropertyChargeSchedulesFilter = {
      ...ListConstants.PROPERTY_CHARGE_SCHEDULES_FILTER_DEFAULT(),
      organizationIds: [org.organizationId],
      includeDeletedSchedules: false
    };

    const response = await this.chargesService.searchPropertyChargeSchedules(
      filter,
      PropertyChargeScheduleListSortOptions.NameAsc,
      0,
      PaginationConstants.PAGINATION_MAX_PAGE_SIZE()
    );

    if (response.success && response.data?.data) {
      this.schedules.set(
        response.data.data.map(s => ({ id: s.propertyChargeScheduleId, name: s.name }))
      );
    }
  }

  public async loadSummary(filter: PropertyChargeFilter): Promise<void> {
    this.isSummaryLoading.set(true);
    try {
      const response = await this.chargesService.getChargeBulkSummary({
        useFilter: true,
        filter,
        selectedIds: null,
        excludedIds: null,
        note: null
      });
      if (response.success) {
        this.summary.set(response.data);
      }
    } finally {
      this.isSummaryLoading.set(false);
    }
  }

  public onFilterChanged(filter: PropertyChargeFilter): void {
    if (this.skipNextSummaryReload) {
      this.skipNextSummaryReload = false;
      return;
    }
    this.loadSummary(filter);
  }

  //#endregion

  //#region Bucket Click

  public onBucketSelected(bucket: InvoiceAgingBucket): void {
    this.selectedBucket.set(bucket);
    const list = this.chargeListRef;
    if (!list) return;

    const baseStatuses = [ChargeStatus.Open, ChargeStatus.Late, ChargeStatus.Pending];
    const thirtyDaysAgoISO = this.getThirtyDaysAgoISO();

    // Bucket clicks only affect the grid; the summary cards are aging-independent
    // by design so they must not re-skeleton when a bucket is selected.
    this.skipNextSummaryReload = true;

    if (bucket === 'current') {
      list.applyExternalFilter({
        statuses: baseStatuses,
        minimumDueDate: thirtyDaysAgoISO,
        maximumDueDate: undefined
      });
    } else if (bucket === 'pastDue') {
      list.applyExternalFilter({
        statuses: baseStatuses,
        maximumDueDate: thirtyDaysAgoISO,
        minimumDueDate: undefined
      });
    } else {
      list.applyExternalFilter({
        minimumDueDate: undefined,
        maximumDueDate: undefined
      });
    }
  }

  private getThirtyDaysAgoISO(): string {
    const date = DateUtilities.startOfToday();
    date.setDate(date.getDate() - 30);
    return DateUtilities.format(date, DateFormat.ISODate);
  }

  //#endregion
}
